import React, { useEffect, useState } from 'react'
import { Button, Select, TextInput } from 'components/Form'
import { ConfirmationModal, Modal, SimpleTable, Tooltip } from 'components/Ui'
import { getApiData, deleteApiRecord, createApiRecord, updateApiRecord } from 'services/ApiConfigurationService'
import { Field, Form, Formik } from 'formik'
import { AiOutlinePlus } from 'react-icons/ai'
import { HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi'
import { Success } from 'utils/alerts'
import * as Yup from 'yup'

const validationSchema = Yup.object().shape({
    name: Yup.string().required('name field is required'),
    company: Yup.string().required('company field is required'),
    endpoint_url: Yup.string().required('endpoint url field is required'),
    http_method: Yup.string().required('http method field is required'),
})

export default function ApiConfigurationTabView({ companyData, record }) {
    const [data, setData] = useState([])
    const [selectedData, setSelectedData] = useState()
    const [open, setOpen] = useState(false)

    useEffect(() => {
        if (record) {
            getData({ company: record?.id })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [record])

    const getData = data => {
        getApiData(data)
            .then(response => {
                if (response.data) {
                    setData(response.data?.results)
                }
            })
            .catch(error => console.log('Error fetching order type data: ', error))
    }

    const handleDelete = id => {
        deleteApiRecord(id)
            .then(() => {
                Success('Deleted Successfully!')
                getData({
                    company: record?.company?.id,
                    customer: record?.id,
                })
            })
            .catch(error => console.log('Error: ', error))
    }

    const handleSubmit = data => {
        if (selectedData?.id) {
            updateApiRecord(selectedData?.id, data)
                .then(() => {
                    getData({ company: record?.id })
                    Success('Updated Successfully!')
                    setOpen(false)
                    setSelectedData()
                })
                .catch(error => console.log('Error: ', error))
        } else {
            data.endpoint_url = `${data.endpoint_url}${record.code}/`
            createApiRecord(data)
                .then(() => {
                    getData({ company: record?.id })
                    Success('Created Successfully!')
                    setOpen(false)
                    setSelectedData()
                })
                .catch(error => console.log('Error: ', error))
        }
    }

    return (
        <div>
            <div className="flex items-center justify-end mb-4">
                <Button
                    className="text-white border-[#F05540] bg-[#F05540] dark:border-[#F05540] dark:bg-[#F05540]"
                    onClick={() => setOpen(true)}
                >
                    <AiOutlinePlus size={20} /> Add New
                </Button>
            </div>
            <div className="grid grid-cols-1 gap-x-4 gap-y-4">
                <SimpleTable>
                    <thead>
                        <SimpleTable.TR>
                            <SimpleTable.TH>Name</SimpleTable.TH>
                            <SimpleTable.TH>Company</SimpleTable.TH>
                            <SimpleTable.TH>http Method</SimpleTable.TH>
                            <SimpleTable.TH>Endpoint url</SimpleTable.TH>
                            <SimpleTable.TH>Action</SimpleTable.TH>
                        </SimpleTable.TR>
                    </thead>
                    <tbody>
                        {(data || [])?.map((row, index) => (
                            <SimpleTable.TR key={`row-${index}`}>
                                <SimpleTable.TD>{row?.name}</SimpleTable.TD>
                                <SimpleTable.TD>{row.company_code}</SimpleTable.TD>
                                <SimpleTable.TD>{row?.http_method}</SimpleTable.TD>
                                <SimpleTable.TD>{row?.endpoint_url}</SimpleTable.TD>
                                <SimpleTable.TD>
                                    <div className="flex items-center justify-center gap-3">
                                        {/* {checkPermission('order-type', 'edit') ? ( */}
                                        <Tooltip title={'Edit'}>
                                            <span
                                                onClick={() => {
                                                    setSelectedData(row)
                                                    setOpen(true)
                                                }}
                                                className="hover:text-primary"
                                            >
                                                <HiOutlinePencil size={20} />
                                            </span>
                                        </Tooltip>
                                        {/* ) : null} */}
                                        {/* {checkPermission('order-type', 'delete') ? ( */}
                                        <Tooltip title={'Delete'}>
                                            <ConfirmationModal
                                                message={'Are you sure you want to delete this record!'}
                                                onConfirm={() => handleDelete(row.id)}
                                            >
                                                <span className="hover:text-danger">
                                                    <HiOutlineTrash size={20} />
                                                </span>
                                            </ConfirmationModal>
                                        </Tooltip>
                                        {/* ) : null} */}
                                    </div>
                                </SimpleTable.TD>
                            </SimpleTable.TR>
                        ))}
                    </tbody>
                </SimpleTable>
            </div>
            <Modal
                show={open}
                maxWidth="4xl"
                onClose={() => {
                    setOpen(false)
                    setSelectedData()
                }}
            >
                <div className="flex flex-col gap-1 m-3">
                    <h2 className="font-semibold text-black text-title-md">
                        {selectedData ? 'Update API Configuration' : 'Add New API Configuration'}
                    </h2>
                </div>
                <div className="flex flex-col items-center">
                    <div className="w-full bg-white border rounded-sm overflow-auto max-h-150 border-stroke shadow-default dark:border-strokedark dark:bg-boxdark">
                        <div className="flex flex-col gap-3 p-6">
                            <Formik
                                initialValues={
                                    selectedData
                                        ? selectedData
                                        : {
                                              company: record?.id || '',
                                              name: '',
                                              endpoint_url: '',
                                              http_method: '',
                                              auth_config: { access_token: '' },
                                          }
                                }
                                validationSchema={validationSchema}
                                onSubmit={values => {
                                    handleSubmit(values)
                                }}
                            >
                                {({ values, touched, errors }) => (
                                    <Form>
                                        <div className="grid grid-cols-1 gap-x-4 gap-y-4 md:grid-cols-4">
                                            <Field name="name">
                                                {({ field, form }) => (
                                                    <TextInput
                                                        type="text"
                                                        className="md:col-span-2"
                                                        label="Name"
                                                        name="name"
                                                        error={errors.name && touched.name}
                                                        message={errors.name}
                                                        value={values?.name}
                                                        onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                    />
                                                )}
                                            </Field>
                                            <Field name="company">
                                                {({ field, form }) => (
                                                    <Select
                                                        id="company"
                                                        name="company"
                                                        label="Company"
                                                        placeholder="Company"
                                                        className="md:col-span-2"
                                                        value={values?.company}
                                                        options={companyData}
                                                        onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                        error={touched.company && errors.company}
                                                        message={errors.company}
                                                        disabled
                                                    />
                                                )}
                                            </Field>
                                            <Field name="endpoint_url">
                                                {({ field, form }) => (
                                                    <TextInput
                                                        type="text"
                                                        className="md:col-span-2"
                                                        label="Endpoint url"
                                                        name="endpoint_url"
                                                        error={errors.endpoint_url && touched.endpoint_url}
                                                        message={errors.endpoint_url}
                                                        value={values?.endpoint_url}
                                                        onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                    />
                                                )}
                                            </Field>
                                            <Field name="http_method">
                                                {({ field, form }) => (
                                                    <TextInput
                                                        type="text"
                                                        className="md:col-span-2"
                                                        label="Http Method"
                                                        name="http_method"
                                                        error={errors.http_method && touched.http_method}
                                                        message={errors.http_method}
                                                        value={values?.http_method}
                                                        onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                    />
                                                )}
                                            </Field>
                                            <Field name="access_token">
                                                {({ field, form }) => (
                                                    <TextInput
                                                        type="text"
                                                        className="md:col-span-2"
                                                        label="Access Token"
                                                        name="access_token"
                                                        error={errors.access_token && touched.access_token}
                                                        message={errors.access_token}
                                                        value={values?.auth_config?.access_token}
                                                        onChange={e => {
                                                            form.setFieldValue(`auth_config.${field.name}`, e.target.value)
                                                        }}
                                                    />
                                                )}
                                            </Field>
                                        </div>
                                        <div className="flex items-center justify-end gap-4 mt-3">
                                            <Button
                                                className="h-10 w-18 text-primary border border-primary dark:text-secondary dark:border-secondary"
                                                type="button"
                                                onClick={() => {
                                                    setOpen(false)
                                                    setSelectedData()
                                                }}
                                            >
                                                Discard
                                            </Button>
                                            <Button
                                                type="submit"
                                                className="flex items-center justify-center px-6 py-2 text-white transition border rounded-lg cursor-pointer hover:bg-opacity-90 border-primary bg-primary dark:border-secondary dark:bg-secondary"
                                            >
                                                Save
                                            </Button>
                                        </div>
                                    </Form>
                                )}
                            </Formik>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    )
}
